import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import useLoader from "../../hooks/useLoader";
import LabourLawSummary from "../../components/labour-law/LabourLawSummary";
import LabourLawFilter from "../../components/labour-law/LabourLawFilter";
import LabourLawList from "../../components/labour-law/LabourLawList";
import Pagination from "../../components/common/Pagination";
import { getLabourLawUsers } from "../../services/labourLaw";
import useAuthUser from "../../hooks/useAuthUser";
import HealthCheckForm from "../../components/labour-law/HealthCheckForm";
import useModal from "../../hooks/useModal";

const LabourLawPage = () => {
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();
  const { isAdminHrRole } = useAuthUser();
  const { showModal, hideModal } = useModal();

  //  Tab State
  const [activeTab, setActiveTab] = useState("summary");

  const getDefaultMonthFilter = () => {
    const d = new Date();
    return {
      month: d.getMonth() + 1,
      year: d.getFullYear(),
    };
  };

  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({});
  const [page, setPage] = useState(1);
  const [activeFilters, setActiveFilters] = useState({
    user_status: "1",
    ...getDefaultMonthFilter(),
  });

  // Redirect non-admin users
  useEffect(() => {
    if (!isAdminHrRole) navigate("/", { replace: true });
  }, [isAdminHrRole, navigate]);

  const getFinancialYear = (month, year) => {
    return month >= 4
      ? `${year}-${year + 1}`
      : `${year - 1}-${year}`;
  };

  const financialYear = getFinancialYear(
    activeFilters.month,
    activeFilters.year
  );

  const refreshUsers = () => {
    fetchUsers(page, activeFilters);
  };

  // Open Health Check modal
  const openHealthCheckModal = (user) => {
    const modalTitle = `${user.name} (${user.employeeCode})`;

    showModal(
      modalTitle,
      <HealthCheckForm
        user={user}
        financialYear={financialYear}
        onSuccess={() => {
          hideModal();
          fetchUsers(page, activeFilters);
        }}
      />
    );
  };

  // Fetch users
  const fetchUsers = async (pageNo = 1, filters = {}) => {
    try {
      showLoader();
      const res = await getLabourLawUsers(pageNo, filters);

      if (res.status === 200) {
        const apiUsers = res.data.data.users || [];

        setUsers(
          apiUsers.map((u) => ({
            userId: u.userId,
            name: u.name,
            employeeCode: u.employeeCode,
            token: u.token,
            compliance: u.compliance,
          }))
        );

        const p = res.data.data.pagination;
        setPagination({
          totalRecords: p.total || apiUsers.length,
          perPage: p.perPage || 20,
          pageNo: p.pageNo || 1,
          totalPages: Math.ceil(
            (p.total || apiUsers.length) / (p.perPage || 20)
          ),
        });
      } else {
        setUsers([]);
        setPagination({});
      }
    } catch (err) {
      console.error(err);
      setUsers([]);
      setPagination({});
    } finally {
      hideLoader();
    }
  };

  // Handle filter
  const handleFilter = (filters = {}) => {
    setActiveFilters(filters);
    setPage(1);
    fetchUsers(1, filters);
  };

  // Export
  const handleExport = async () => {
    try {
      showLoader();
      const response = await getLabourLawUsers(1, {
        ...activeFilters,
        export: 1,
      });
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Labour_Law_Compliance_${activeFilters.month}_${activeFilters.year}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.log("error while exporting labourLaw", error);
    } finally {
      hideLoader();
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchUsers(1, activeFilters);
    // eslint-disable-next-line
  }, []);

  if (!isAdminHrRole) return null;

  return (
    <div>
      <Header title="Labour Law Compliance Management" />

      {/*  Tabs */}
      <div className="d-flex gap-2 mb-3 mt-3">
        <button
          onClick={() => setActiveTab("summary")}
          className={`btn ${
            activeTab === "summary" ? "btn-success" : "btn-outline-secondary"
          }`}
        >
          Overview
        </button>

        <button
          onClick={() => setActiveTab("details")}
          className={`btn ${
            activeTab === "details" ? "btn-success" : "btn-outline-secondary"
          }`}
        >
          Employee Compliance
        </button>
      </div>

      {/*  Tab 1: Summary */}
      {activeTab === "summary" && (
        <LabourLawSummary onBulkSuccess={refreshUsers} />
      )}

      {/*  Tab 2: Details */}
      {activeTab === "details" && (
        <>
          <LabourLawFilter
            onFilter={handleFilter}
            onExport={handleExport}
          />

          <LabourLawList
            users={users}
            onHealthCheckAction={openHealthCheckModal}
          />

          {pagination?.totalPages >= 1 && (
            <Pagination
              totalPages={pagination.totalPages}
              currentPage={pagination.pageNo}
              perPage={pagination.perPage}
              totalRecords={pagination.totalRecords}
              onPageChange={(p) => {
                setPage(p);
                fetchUsers(p, activeFilters);
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default LabourLawPage;